import React from 'react';

const LegacyExperienceSection = () => (
  <section className="px-6 py-20 md:py-28 bg-[#0f1115]" id="experience">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-[#f8f5ed] mb-6">
          Built for real chamas.
          <br />
          <span className="text-emerald-400">Not spreadsheet experts.</span>
        </h2>
        <p className="text-xl text-[#d8d2c5] max-w-3xl mx-auto">
          We talked to treasurers, secretaries, and members. Here&apos;s what they told us they
          needed:
        </p>
      </div>

      {/* Testimonial-style feature blocks */}
      <div className="grid lg:grid-cols-3 gap-8 mb-16">
        <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-6">
          <div className="text-4xl mb-4">💬</div>
          <p className="text-[#f8f5ed] text-lg font-medium mb-3 leading-relaxed">
            &quot;Stop asking me if I received your payment. I want to see it the second you send
            it.&quot;
          </p>
          <p className="text-sm text-emerald-300 font-medium">→ Real-time M-Pesa sync</p>
          <p className="text-xs text-[#d8d2c5] mt-2">
            Payments show up instantly. No more phone calls.
          </p>
        </div>

        <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-6">
          <div className="text-4xl mb-4">🤷‍♀️</div>
          <p className="text-[#f8f5ed] text-lg font-medium mb-3 leading-relaxed">
            &quot;I don&apos;t have time to learn a complicated system. Just make it work.&quot;
          </p>
          <p className="text-sm text-cyan-300 font-medium">→ 5-minute setup</p>
          <p className="text-xs text-[#d8d2c5] mt-2">Add members, set contribution amount, done.</p>
        </div>

        <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-6">
          <div className="text-4xl mb-4">🔍</div>
          <p className="text-[#f8f5ed] text-lg font-medium mb-3 leading-relaxed">
            &quot;Members keep asking where their money went. I need receipts for everything.&quot;
          </p>
          <p className="text-sm text-amber-300 font-medium">→ Automatic audit trail</p>
          <p className="text-xs text-[#d8d2c5] mt-2">Every transaction logged. Export anytime.</p>
        </div>
      </div>

      {/* Feature highlight grid */}
      <div className="bg-gradient-to-br from-emerald-500/5 to-cyan-500/5 border border-emerald-500/10 rounded-3xl p-8 md:p-12">
        <h3 className="text-2xl font-bold text-[#f8f5ed] mb-8 text-center">
          Plus all the details that matter
        </h3>
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-emerald-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div>
              <h4 className="text-[#f8f5ed] font-semibold mb-1">Role-based access</h4>
              <p className="text-sm text-[#d8d2c5]">
                Admins approve loans. Treasurers see money. Members see their own records.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-cyan-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <h4 className="text-[#f8f5ed] font-semibold mb-1">Mobile-first design</h4>
              <p className="text-sm text-[#d8d2c5]">
                Works perfectly on phones. Because that&apos;s what everyone uses.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-amber-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </div>
            <div>
              <h4 className="text-[#f8f5ed] font-semibold mb-1">Smart reminders</h4>
              <p className="text-sm text-[#d8d2c5]">
                Automatic notifications before contributions are due. Less chasing.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-purple-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <div>
              <h4 className="text-[#f8f5ed] font-semibold mb-1">Bulk operations</h4>
              <p className="text-sm text-[#d8d2c5]">
                Import 50 members at once. Export reports for your AGM. One click.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default LegacyExperienceSection;
